import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";

import useEbay from "../../data/useEbay";
import { IPart } from "../../models/Part";
import PopOutBox from "../shared/PopOutBox";
import useOffers from "../../data/useOffers";
import classNames from "../../utils/classNames";
import LoadingWheel from "../shared/LoadingWheel";
import EbayListingTitle from "./EbayListingTitle";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import EditListingSpecifics from "./EditListingSpecifics";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { EbayCategoryDropdown } from "./EbayCategoryDropdown";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import { EbayConditionDropdown } from "./EbayConditionDropdown";
import useNotification from "../notifications/useNotifications";
import CustomJoditEditor from "../shared/Inputs/CustomJoditEditor";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { EbayStoreCategoryDropdown } from "./EbayStoreCategoryDropdown";
import usePartAspectsMutations from "../../data/usePartAspectsMutations";
import usePartDefaultCategories from "../../data/usePartDefaultCategories";
import checkEbayDisabled, { ebayChecks } from "../../utils/checkEbayDisabled";
import { FormatType, IEbayOffer, ListingDuration } from "../../models/EbayOffer";
import useTemplateProductDescription from "../../data/useTemplateProductDescription";
import useSettings from "../../data/useSettings";


export type EbayListingFormValues = {
  title: string;
  description: string;
  format: FormatType;
  duration: ListingDuration;
  listingStarted: string | null;
  listNow: boolean;
  auctionStartPrice: number | null;
  auctionReservePrice: number | null;
  buyItNowPrice: number | null;
  enableBestOffer: boolean | null;
  autoAcceptPrice: number | null;
  locationKey: any;
  fulfillmentPolicyId: any;
  paymentPolicyId: any;
  returnPolicyId: any;
  categoryId: any;
  condition: any;
  conditionDescription: string;
};

const formatOptions = [
  { id: FormatType.Auction, name: "Auction" },
  { id: FormatType.FixedPrice, name: "Buy It Now" },
];

const durationOptions = [
  // { id: ListingDuration.GTC, name: "Until Cancelled" }, //auto selected on 
  { id: ListingDuration.Days1, name: "1 Day" },
  { id: ListingDuration.Days3, name: "3 Days" },
  { id: ListingDuration.Days5, name: "5 Days" },
  { id: ListingDuration.Days7, name: "7 Days" },
  { id: ListingDuration.Days10, name: "10 Days" },
  { id: ListingDuration.Days21, name: "21 Days" },
  { id: ListingDuration.Days30, name: "30 Days" },
];

const defaultValues = {
  fulfillmentPolicyId: "125494812024",
  paymentPolicyId: "121933662024",
  returnPolicyId: "204693254024",
};

export default function EditListing({ part, page, setPage }: { part: IPart, page: number, setPage: React.Dispatch<React.SetStateAction<number>> }) {
  const { defaultPartCategory, useDefaultPartCategory } = usePartDefaultCategories({ part: part });
  const { createCustomAspects } = usePartAspectsMutations();
  const { create, update } = useOffers();
  const { policies, locations } = useEbay();
  const { withdraw, createOffer } = useSinglePartMutations(part.id);
  const { productDescription, updateProductDescription } = useTemplateProductDescription(part);

  const { saveHandlers } = useDefaultCRUDHandlers("Offer");
  const { addNotification } = useNotification();

  const [isLoading, setIsLoading] = useState(true);
  const [withdrawIsLoading, setWithdrawIsLoading] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [applyDefaultsOpen, setApplyDefaultsOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [description, setDescription] = useState<string>("");
  const [error, setError] = useState<string>("");
  // const [page, setPage] = useState<number>(0);
  const [isHovering, setIsHovering] = useState(false);

  const defaultVals = {
    title: `${part.vehicle?.dvlaYearOfManufacture} ${part.vehicle?.combinedMake} ${part.vehicle?.combinedModel} ${part.title}`,
    format: FormatType.FixedPrice,
    duration: ListingDuration.GTC,
    listingStarted: DateTime.now().plus({ minutes: 30 }).toFormat("yyyy-MM-dd'T'HH:mm"),
    auctionStartPrice: 0,
    auctionReservePrice: 0,
    buyItNowPrice: 0,
    enableBestOffer: false,
    autoAcceptPrice: 0,
    fulfillmentPolicyId: undefined,  //Policies are loaded in from the category defaults
    paymentPolicyId: undefined,
    returnPolicyId: undefined,
    categoryId: undefined, //SET TO UNDEFINED AS GRABS A VALUE IN THE CUSTOM SELECT
    storeCategoryId: undefined, //SET TO UNDEFINED AS GRABS A VALUE IN THE CUSTOM SELECT
    locationKey: undefined,
    condition: 3000,
    conditionDescription: "Used - ",
    listNow: true,
  };

  const { register, getValues, setValue, watch, handleSubmit, reset, formState, control } =
    useForm<EbayListingFormValues>({
      defaultValues: defaultVals
    });

  const onSubmit = (data: EbayListingFormValues, nextPage: boolean) => {
    let partOffer = {
      ...part.offers[0],
      ...data,
      partDataId: part.id,
      quantity: 1,
      description: description,
      // condition: parseInt(data.condition),
    };

    if (partOffer.enableBestOffer) {
      const partCategoryDefault = defaultPartCategory.data?.data;
      const hasAutoAcceptPrice = !!(partOffer.autoAcceptPrice && partOffer.autoAcceptPrice > 0);
      const hasAutoAcceptThreshold = !!(partCategoryDefault?.autoAcceptThreshold && partCategoryDefault.autoAcceptThreshold > 0);

      if (!partOffer.buyItNowPrice) {
        addNotification({
          primaryText: "Buy It Now Price is required if enable best offer is enabled",
          variant: "error",
        });
        return;
      }

      if (!hasAutoAcceptPrice && !hasAutoAcceptThreshold) {
        addNotification({
          primaryText: "Auto Accept Price is required if enable best offer is enabled (or set an Auto Accept Threshold on the part category defaults)",
          variant: "error",
        });
        return;
      }

      // If no explicit auto accept price, fall back to the category threshold (% off Buy It Now)
      let effectiveAutoAcceptPrice = partOffer.autoAcceptPrice;
      if (!hasAutoAcceptPrice && hasAutoAcceptThreshold && partCategoryDefault?.autoAcceptThreshold) {
        effectiveAutoAcceptPrice = partOffer.buyItNowPrice - ((partOffer.buyItNowPrice * partCategoryDefault.autoAcceptThreshold) / 100);
      }

      if (effectiveAutoAcceptPrice && effectiveAutoAcceptPrice > partOffer.buyItNowPrice) {
        addNotification({
          primaryText: "Auto Accept Price cannot be higher than Buy It Now Price",
          variant: "error",
        });
        return;
      }
    } else {
      partOffer.autoAcceptPrice = null;
    }



    if (!partOffer.id) {
      create.mutate(partOffer, {
        ...saveHandlers,
        onSuccess: () => {
          if (nextPage) setPage(1);
          saveHandlers.onSuccess();
        },
      });
    } else {
      update.mutate(partOffer, {
        ...saveHandlers,
        onSuccess: () => {
          if (nextPage) setPage(1);
          saveHandlers.onSuccess();
        },
      });
    }
  };

  const handleSetFormat = (_: string, value: any) => {
    setValue(
      "duration",
      value === FormatType.FixedPrice
        ? ListingDuration.GTC
        : ListingDuration.Days30
    );
    setValue("format", value);
  };

  const handleIsDisabled = () => {
    const offerData: EbayListingFormValues = getValues();

    // If "List Now" is checked, ignore listing time validation
    const ignoreListingTime = offerData.listNow;

    const offer = offerData as IEbayOffer;

    const partCategoryDefault = defaultPartCategory.data?.data;

    let result: ebayChecks = checkEbayDisabled({ part, offer, partCategoryDefault, ignoreListingTime });

    setError(result.error);
    // setIsDisabled(result.isDisabled);

    if (!part.offers || part.offers.length === 0) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  };

  const handleWithdraw = () => {
    if (!part.offers[0].publishedOn)
      return;

    withdraw.mutate(part.offers[0].id, {
      onSuccess: () => {
        setWithdrawOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      }
    });
  };

  const handleApplyDefaults = () => {
    setIsLoading(true);

    useDefaultPartCategory.mutate(undefined, {
      onSuccess: () => {
        setApplyDefaultsOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
      onSettled: () => {
        setIsLoading(false);
      }

    });
  };

  const handleSetFirstLocation = () => {
    const data = getValues();

    const offer = part.offers?.[0];

    if (!data.locationKey && locations.data && locations.data.data.locations.length > 0) {
      if (offer && offer.locationKey) return;

      const firstWithName = locations.data.data.locations.filter(l => l.name && l.name.trim() !== "")?.[0];

      setValue("locationKey", firstWithName?.id);
    }
  }

  useEffect(() => {
    if (!policies.isLoading && policies.data && !locations.isLoading && locations.data && !defaultPartCategory.isLoading && !productDescription.isLoading) {
      // console.log("Policies and Locations loaded");
      if (isLoading) {
        //Add default custom aspects
        createCustomAspects.mutate(part.id);
      }

      if (part.offers.length === 0) {

        // console.log("New Offer is Being Created!");
        createOffer.mutate(part.id); // API call
        handleSetFirstLocation();

      } else if (part.offers?.length > 0) {
        // console.log(part.offers[0])

        //Map default values onto offer where offer value is null
        let offer = Object.fromEntries(
          Object.entries(part.offers[0]).map(([key, value]) => [
            key,
            value !== null ? value : Object.entries(defaultVals).filter(x => x[0] == key)[0]?.[1] ?? null,
          ])
        );


        reset(offer);
        //Set description
        setDescription(part.offers[0]?.description ?? productDescription.data?.data)
        handleSetFirstLocation();
        setIsLoading(false);

      } else {
        //Generate description
        handleSetFirstLocation();
        setDescription(productDescription.data?.data ?? "");
        setIsLoading(false);
      }

      // setIsLoading(false);
      handleIsDisabled();
    }
  }, [part, policies.data, policies.isLoading, locations.data, locations.isLoading, defaultPartCategory.isLoading, productDescription.isLoading]);


  const handleSetDescription = async () => {
    // console.log("Setting Description from Template", productDescription?.data);
    await updateProductDescription.mutateAsync(part.id)
  }

  //Check if form is valid
  useEffect(() => {
    handleIsDisabled();
    // console.log(getValues())
  }, [formState]);

  //Scroll to top on page load - this is mostly for when you go back from the second page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const bestOfferValue = watch("enableBestOffer");

  const isPublished = useMemo(() => {
    return part.offers && part.offers.length > 0 && part.offers[0].publishedOn !== null;
  }, [part.offers]);

  if (isLoading) {
    return (
      <div className="mt-[20vh]">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <>
      <ConfirmModal open={withdrawOpen} setOpen={setWithdrawOpen} isLoading={withdrawIsLoading}
        title={"Withdraw Listing?"}
        message={`Are you sure you want to withdraw this listing? \nThis will remove it from eBay and cannot be undone.`}
        confirmButtonText={"Withdraw Listing"}
        onConfirm={() => handleWithdraw()}
      />
      <ConfirmModal open={applyDefaultsOpen} setOpen={setApplyDefaultsOpen} isLoading={false}
        title={"Apply Defaults to Listing?"}
        message={`Are you sure you want to apply defaults to this listing? \nThis will change all the current information displayed.`}
        confirmButtonText={"Apply Defaults"}
        onConfirm={() => handleApplyDefaults()}
      />
      <div className="space-y-4">

        {/* Move from original form to specifics */}
        {page === 0 ? (
          <form onSubmit={handleSubmit(() => onSubmit(getValues(), false))} >
            <div className="grid grid-cols-1 pb-20 sm:grid-cols-2 gap-4">

              <EbayListingTitle part={part} register={register} getValues={getValues} watch={watch} setValue={setValue} />

              <div className="sm:col-span-2 -mt-10 flex justify-end items-center">
                <ButtonInput label="Reset Description" onClick={handleSetDescription} classes={undefined} isLoading={updateProductDescription.isLoading} />
              </div>

              <div className="sm:col-span-2">
                <CustomJoditEditor
                  content={description}
                  setContent={setDescription}
                  placeholder="Create your listing description..."
                  height={300}
                />
              </div>

              <SelectInput
                label={"Listing Format"}
                register={register}
                registerName={"format"}
                options={formatOptions}
                setValue={handleSetFormat}
                watch={watch}
              />

              {getValues("format") === FormatType.Auction ? (
                <SelectInput
                  label={"Duration"}
                  register={register}
                  registerName={"duration"}
                  options={durationOptions}
                  setValue={setValue}
                  watch={watch}
                />
              ) : (
                <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
              )}

              <div className="sm:col-span-1 flex gap-x-2">
                <div className="flex items-center">
                  <input
                    id="listNow"
                    {...register("listNow")}
                    type="checkbox"
                    className="mr-2 h-5 w-5"
                    disabled={part.soldDate !== null}
                  />
                  <label htmlFor="listNow">List Now</label>
                </div>

                {!watch("listNow") && (
                  <div className="flex-1">
                    <DateTimeInput
                      label={"Listing Start"}
                      register={register}
                      registerName={"listingStarted"}
                      min={DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm")}
                    />
                  </div>
                )}
              </div>

              <EbayCategoryDropdown
                categoryId={part.offers.length > 0 ? part.offers[0]?.categoryId : defaultPartCategory.data?.data?.ebayCategoryId}
                register={register}
                registerName={"categoryId"}
                setValue={setValue}
                watch={watch}
              />

              <EbayStoreCategoryDropdown
                categoryId={part.offers.length > 0 ? part.offers[0]?.storeCategoryId : defaultPartCategory.data?.data?.ebayStoreCategoryId}
                register={register}
                registerName={"storeCategoryId"}
                setValue={setValue}
                watch={watch}
              />

              <div>
                {/* spacer */}
              </div>

              {getValues("format") === FormatType.Auction ? (
                <>
                  <NumberInput
                    label={"Starting Price (£)"}
                    register={register}
                    registerName={"auctionStartPrice"}
                  />
                  <NumberInput
                    label={"Reserve Price (£)"}
                    register={register}
                    registerName={"auctionReservePrice"}
                  />
                </>
              ) : (
                <>
                  <NumberInput
                    label={"Buy It Now Price (£)"}
                    register={register}
                    registerName={"buyItNowPrice"}
                  />
                  {bestOfferValue ? (
                    <NumberInput
                      label={"Best Offer Auto Accept Price (£)"}
                      register={register}
                      registerName={"autoAcceptPrice"}
                    />
                  ) : (
                    <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
                  )}
                </>
              )}

              {getValues("format") === FormatType.FixedPrice && (
                <>
                  <div className="sm:col-span-1 mt-4 flex gap-x-2">
                    <input
                      id="enableBestOffer"
                      {...register("enableBestOffer")}
                      type="checkbox"
                      className={classNames((part.soldDate !== null || isPublished) && "text-gray-400", "h-5 w-5")}
                      disabled={part.soldDate !== null || isPublished}
                    />
                    <label htmlFor="enableBestOffer">Allow Best Offer?</label>
                    {isPublished && (
                      <PopOutBox classes="w-96 z-30">
                        <p>Ebay does not allow the Best Offer setting to be changed after a listing has been published.</p>
                        <p>If you want to change this setting, you will need to withdraw the listing and create a new one.</p>
                      </PopOutBox>
                    )}
                  </div>

                  <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
                </>
              )}

              {/* Policies */}
              <SelectInput
                label={"Shipping Policy*"}
                register={register}
                registerName={"fulfillmentPolicyId"}
                options={policies.data?.data.fulfilmentPolicies}
                setValue={setValue}
                watch={watch}
              />
              <SelectInput
                label={"Payment Policy*"}
                register={register}
                registerName={"paymentPolicyId"}
                options={policies.data?.data.paymentPolicies}
                setValue={setValue}
                watch={watch}
              />
              <SelectInput
                label={"Returns Policy*"}
                register={register}
                registerName={"returnPolicyId"}
                options={policies.data?.data.returnPolicies}
                setValue={setValue}
                watch={watch}
              />

              {/* Locations */}
              <SelectInput
                label={"Location of Part*"}
                register={register}
                registerName={"locationKey"}
                options={locations.data?.data.locations}
                setValue={setValue}
                watch={watch}
              />

              {/* Condition */}
              {getValues("categoryId") ? (
                <EbayConditionDropdown
                  categoryId={getValues("categoryId")}
                  register={register}
                  registerName={"condition"}
                  setValue={setValue}
                  watch={watch}
                />
              ) : (
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Condition*
                  </label>
                  <select className="text-sm bg-gray-400" value={1} disabled >
                    <option value={1} disabled>Pick a Category</option>
                  </select>
                </div>

              )}

              {
                watch("condition") < 2000 ? null :
                  <TextInput
                    register={register}
                    registerName={"conditionDescription"}
                    label={"Condtion Description*"}
                    richText
                    rows={1}
                  />
              }

              <div className="sm:col-span-2 mt-4 flex justify-between">
                <div className="flex items-start gap-x-4">
                  {/* Withdraw button shows if listing has been published and not sold */}
                  {part.offers.length > 0 && part.offers[0].publishedOn !== null && !part.soldDate && (
                    <ButtonInput
                      label={"Withdraw Listing"}
                      isSubmit={false}
                      onClick={() => setWithdrawOpen(true)}
                      classes={"!bg-red-500 hover:!bg-red-600"}
                    />
                  )}
                  {part.offers.length > 0 && !part.soldDate && (
                    <div className="flex flex-col">
                      <ButtonInput
                        label={"Apply Defaults"}
                        isSubmit={false}
                        onClick={() => setApplyDefaultsOpen(true)}
                        classes={""}
                        disabled={!defaultPartCategory.data?.data}
                      />
                      {
                        !defaultPartCategory.data?.data &&
                        <span className="text-gray-600 text-sm">No defaults found</span>
                      }
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-4">
                  <ButtonInput
                    label={"Save"}
                    isSubmit={true}
                    onClick={undefined}
                    classes={undefined}
                    disabled={part.soldDate !== null}
                    isLoading={create.isLoading || update.isLoading}
                  />
                  <div
                    onMouseOver={() => setIsHovering(true)}
                    onMouseOut={() => setIsHovering(false)}
                    className="relative"
                  >
                    {isHovering && error && (
                      <div className="absolute bottom-10 w-[250px] bg-white border-2 border-gray-200">
                        <p className=" text-red-700 p-2">
                          {error}
                        </p>
                      </div>
                    )}
                    <ButtonInput
                      label={"Save & Next"}
                      isSubmit={false}
                      onClick={() => onSubmit(getValues(), true)}
                      classes={"!bg-white !border-1 !border-gray-400 !text-gray-600 hover:!bg-gray-200"}
                      disabled={isDisabled}
                      isLoading={create.isLoading || update.isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>

        ) : (
          <EditListingSpecifics part={part} setPage={setPage} />
        )}

      </div>
    </>
  );
}
